import { Component, Input, OnChanges } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { TranslateService } from '@ngx-translate/core';
import { HeatmapData } from '../../../interfaces/metrics';

// Fixed heatmap y-axis day order; maps to the weekday_short i18n dictionary keys.
const HEATMAP_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];

// ISO-8601 week number for a YYYY-MM-DD Monday. Same algorithm as the dashboard grid's
// weekNo(): shift to the Thursday of that week, then count weeks from 4 January.
function isoWeek(iso: string): number {
  const d = new Date(iso + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + 3 - ((d.getUTCDay() + 6) % 7));
  const week1 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getUTCDay() + 6) % 7)) / 7);
}

@Component({
  selector: 'bst-hours-heatmap',
  templateUrl: './hours-heatmap.component.html',
  styleUrls: ['./hours-heatmap.component.scss'],
  standalone: false
})
export class HoursHeatmapComponent implements OnChanges {
  @Input() data!: HeatmapData;
  options: EChartsOption = {};

  constructor(private translate: TranslateService) {}

  ngOnChanges(): void {
    if (!this.data) { return; }
    this.options = {
      grid: { left: 26, right: 4, top: 4, bottom: 20 },
      xAxis: {
        type: 'category',
        // Real ISO week numbers, not w1..w16. Every third label so 16 columns stay legible
        // at any tile width; showMaxLabel pins the most recent week, which is the one a
        // reader looks for first.
        data: this.data.weekStarts.map(w => 'w' + isoWeek(w)),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#6B7A89', fontWeight: 600, fontFamily: 'inherit', interval: 2, showMaxLabel: true }
      },
      yAxis: {
        type: 'category',
        data: HEATMAP_DAYS.map(d => this.translate.instant('weekday_short.' + d)),
        // An ECharts category axis puts index 0 at the BOTTOM, so without this the week
        // paints Friday to Monday, top to bottom.
        inverse: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#6B7A89', fontWeight: 600, fontFamily: 'inherit' }
      },
      visualMap: {
        show: false,
        min: 0,
        max: this.data.maxHours,
        inRange: { color: ['#EEF1F5', '#CFE0F8', '#9FC2F1', '#5E93E6', '#1863DC'] }
      },
      series: [
        {
          type: 'heatmap',
          data: this.data.cells.map(c => [c.weekIndex, c.dayIndex, c.hours]),
          itemStyle: { borderColor: '#fff', borderWidth: 2, borderRadius: 3 }
        }
      ],
      animation: false
    };
  }
}
